/* eslint-disable class-methods-use-this */
/**
@format
@flow
*/
import { Auth } from 'aws-amplify';
import ActionTypes from './redux/actionTypes';
import { store } from './redux/store';

const API_ROUTES = {
  local: 'http://localhost:3000',
  development: 'https://api-dev.teamziynx.com',
  staging: 'https://api-staging.teamziynx.com',
  production: 'https://api.teamziynx.com',
};

const isDev: boolean = process.env.REACT_APP_DEVELOPMENT === 'true';
const isStaging: boolean = process.env.REACT_APP_STAGING === 'true';
let API_ROUTE = API_ROUTES.production;

if (isDev) {
  API_ROUTE = API_ROUTES.development;
} else if (isStaging) {
  API_ROUTE = API_ROUTES.staging;
}

export const API_CONFIG = {
  GET_COMPANIES: '/companies',
  POST_COMPANY: '/companies',
  POST_INVITE: '/companies/:id/invite',
  PATCH_INVITE: '/invite/:token',
  GET_INVITE: '/invite/:token',
  GET_USER: '/profiles/:id',
  PATCH_USER: '/profiles/:id',
  RESEND_INVITE: '/invite/:token/resend',
  REMOVE_INVITE: '/invite/:token',
  GET_TEAM_LIST: '/companies/:id/getAllMembers',
  REMOVE_TEAM_MEMBER: '/companies/:id/removeUser/:userId',
  PATCH_COMPANY: '/companies/:id',
  GET_COMPANY: '/companies/:id',
  UPDATE_COMPANY: '/companies/:id',
  GET_PROFILE: '/profiles/:id',
  GET_PROFILE_RESPONSES: '/profiles/:id/responses',
  PATCH_PROFILE: '/profiles/:id',
  VIEWED_PROFILE: '/profile/:id/view',
  PUT_TALENT_NOTES: '/profiles/:id/notes',

  CREATE_MEDIA_URL: '/media/presign',
  GET_UPLOAD_STATUS: '/media/:asset_id',
  EDIT_MEDIA_ASSET: '/media/:asset_id',
  GET_USER_INIT: '/user/init',
  GET_USER_INFO: '/user/:id/settings',
  PATCH_USER_INFO: '/user/:id/settings',
  GET_WATCHLIST: '/watchlist/:id',
  ADD_TO_WATCHLIST: '/watchlist/add',
  REMOVE_FROM_WATCHLIST: '/watchlist/remove',
  GET_DISCOVER: '/discover',
  POST_DISCOVER_FILTER: '/discover',
  GET_CHANNEL: '/discover/:channelId',
  GET_STATEMENTS: '/companies/:id/content',
  POST_QUESTION: '/statements/question',
  GET_QUESTION: '/statements/question/:id',
  PATCH_QUESTION: '/statements/question/:id',
  REMOVE_QUESTION: '/statements/question/:id',
  GET_QUESTION_RESPONSES: '/statements/question/:id/responses',
  VIEWED_RESPONSE: '/statement/:id/view',
  VIEWED_MYSHOT: '/statement/:id/view',
  // Jobs
  GET_JOBS: '/companies/:id/jobs',
  POST_JOB: '/jobs',
  GET_JOB: '/jobs/:id',
  PATCH_JOB: '/jobs/:id',
  REMOVE_JOB: '/jobs/:id',
  // NIL
  GET_ALL_NIL: '/nil',
  GET_ONE_NIL: '/nil/:id',
  POST_NIL: '/nil',
  PATCH_NIL: '/nil/:id',
  DELETE_NIL: '/nil/:id',
  GET_NIL_APPLICATIONS: '/nil/:id/applications',
  // Originals
  GET_ALL_ORIGINALS: '/originals',
  GET_ONE_ORIGINAL: '/originals/:id',
  POST_ORIGINAL: '/originals',
  PATCH_ORIGINAL: '/originals/:id',
  DELETE_ORIGINAL: '/originals/:id',
  POST_TRACKING_PLAY: '/statements/response/:id/action',
  POST_TRACKING_VIEW: '/statements/response/:id/action',
  // Search
  GET_LOCATIONS_SEARCH: '/location/search',
  GET_JOB_LOCATIONS_SEARCH: '/places/search',
  GET_INTERESTS_SEARCH: '/interests/search',
  GET_GRADUATION_YEARS_SEARCH: '/graduation/years/search',
  GET_CHARACTER_TRAITS_SEARCH: '/statements/slots/:slotId',
  GET_COLLEGE_LEAGUE_SEARCH: '/statements/slots/:slotId/options',
  GET_COMPANIES_SEARCH: '/companies/search',
  GET_COMPANY_QUESTIONS_SEARCH: '/companies/search/questions',
  // Intake
  POST_INTAKE_SUBMISSION: '/user/support',
  // ADMIN
  GET_TALENT_SCORECARDS: '/admin/talent-cards',
  GET_TALENT_SCORECARD: '/admin/talent-cards/:id',
  GET_TALENT_SCORECARD_VALUES: '/admin/talent-values',
  PATCH_TALENT_SCORECARD: '/admin/talent-cards/:id',
};

export type APIConfig = typeof API_CONFIG;

function constructRoute(routeID: string, params: { [name: string]: string }, externalRoute) {
  if (externalRoute) return routeID;

  const path = API_CONFIG[routeID];

  if (!params) return `${API_ROUTE}${path}`;

  const keys: string[] = Object.keys(params);

  const parts: string[] = path.split('/');
  keys.forEach((key) => {
    const findKey = `:${key}`;
    if (parts.indexOf(findKey) > -1) {
      parts[parts.indexOf(findKey)] = params[key];
    }
  });
  const interpolatedRoute: string = parts.join('/');
  return `${API_ROUTE}${interpolatedRoute}`;
}

function constructQueryParams(qs = {}): string {
  const queryParams = Object.keys(qs)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(qs[k])}`)
    .join('&');
  if (!queryParams) return '';
  return `?${queryParams}`;
}

function getBody(body: mixed) {
  if (body instanceof ArrayBuffer) return body;
  return JSON.stringify(body);
}

const getToken = async (): Promise<string> => {
  const { refreshToken, accessToken } = await Auth.currentSession();
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const appState = store.getState();
  const companySetupTest = /company/g;

  return new Promise((resolve) => {
    if (appState?.auth?.refreshToken) {
      cognitoUser.refreshSession(refreshToken, (err, session) => {
        const { accessToken: newToken } = session || {};
        store.dispatch({
          type: ActionTypes.auth.refreshToken,
          refreshToken: false,
          companyGroups: (newToken?.payload['cognito:groups'] || []).filter((group) => companySetupTest.test(group)),
        });
        resolve(newToken?.jwtToken);
      });
    } else {
      resolve(accessToken?.jwtToken);
    }
  });
};

const constructHeaders = async (
  contentType: string = 'application/json',
  externalRoute: boolean = false,
  authorization: 'none' | 'optional' | 'required' = 'required',
): Promise<HeadersInit> => {
  let token: ?string;
  if (!externalRoute && authorization !== 'none') {
    try {
      token = await getToken();
    } catch (err) {
      // throw if error is not related to user not being logged in
      if (err !== 'No current user') throw err;
      // throw if auth is required, but no user is logged in
      if (!authorization || authorization === 'required') {
        throw Error('Route requires authorization: user is not logged in');
      }
      // otherwise, authorization is optional, silently ignore
    }
  }
  return {
    'Content-Type': contentType || 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const apiRequest = async (
  route: $Keys<APIConfig>,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT' = 'GET',
  {
    query,
    routeParams,
    body,
    contentType,
    externalRoute,
    authorization = 'required',
  }: {
    body?: string | Object | ArrayBuffer,
    query: { [name: string]: string },
    routeParams: { [name: string]: string },
    contentType?: string,
    externalRoute?: boolean,
    authorization?: 'none' | 'optional' | 'required',
  } = {},
): Promise<Object | String> => {
  const path = constructRoute(route, routeParams, externalRoute);
  const queryParams = constructQueryParams(query);
  const constructedHeaders = await constructHeaders(
    contentType,
    externalRoute,
    authorization,
  );
  const routeConfig: RequestOptions = {
    body: getBody(body),
    method,
    headers: constructedHeaders,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
  };

  const response = await fetch(`${path}${queryParams}`, routeConfig);

  try {
    const json = await response.json();
    if (!response.ok) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.error('Fetch error', {
          request: {
            route: path,
            config: routeConfig,
          },
          response: json,
        });
      }
      throw response;
    }
    return json;
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    if (!response.ok) throw response;
    return response;
  }
};

export default { apiRequest, getToken };
